/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import usePermissionGuard from "../../hooks/usePermissionGuard";
import { Breadcrumbs, Container, Link as BCLink, Box, CircularProgress } from "@mui/material";
import { NavigateNext } from "@mui/icons-material";
import axios from "../../axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : null;
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : null;

// ─── Alert banner ─────────────────────────────────────────────────────────────
const Alert = ({ type = "error", children }) => {
  const styles = {
    error:   { bg: "#fef2f2", border: "#fca5a5", text: "#991b1b" },
    warning: { bg: "#fffbeb", border: "#fcd34d", text: "#92400e" },
  };
  const s = styles[type];
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8,
      padding: "11px 16px", marginBottom: 12, fontSize: 13, color: s.text,
      display: "flex", alignItems: "center", gap: 8,
    }}>
      <span style={{ fontSize: 15 }}>{type === "error" ? "⚠️" : "⏰"}</span>
      <span>{children}</span>
    </div>
  );
};

// ─── KPI card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, accent, icon, onClick }) => (
  <div
    onClick={onClick}
    style={{
      flex: "1 1 160px", minWidth: 150,
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderTop: `3px solid ${accent}`,
      borderRadius: 10,
      padding: "18px 20px",
      cursor: onClick ? "pointer" : "default",
      transition: "box-shadow .15s, transform .15s",
      boxSizing: "border-box",
    }}
    onMouseEnter={e => { if (onClick) { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,.08)"; e.currentTarget.style.transform = "translateY(-1px)"; }}}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 26, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 5, fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>{sub}</div>}
      </div>
      {icon && (
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: accent + "18",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 17, flexShrink: 0,
        }}>
          {icon}
        </div>
      )}
    </div>
  </div>
);

// ─── Section heading ──────────────────────────────────────────────────────────
const SectionHeading = ({ children, sub }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{children}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
  </div>
);

// ─── Bar chart ────────────────────────────────────────────────────────────────
const OrderChart = ({ data, ordersWeek, ordersMonth }) => {
  const max = Math.max(...data.map(d => d.count), 1);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div style={{
      background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
      padding: "20px 24px", marginBottom: 24,
    }}>
      <SectionHeading sub={`${ordersWeek} this week · ${ordersMonth} this month`}>
        Orders — last 30 days
      </SectionHeading>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 72 }}>
        {data.map((d, i) => {
          const isToday = d.date === today;
          const h = Math.max((d.count / max) * 100, d.count > 0 ? 6 : 1.5);
          return (
            <div
              key={i}
              title={`${d.date}: ${d.count} order${d.count !== 1 ? "s" : ""}`}
              style={{
                flex: 1, height: `${h}%`,
                borderRadius: "3px 3px 0 0",
                background: isToday ? "#6366f1" : "#c7d2fe",
                transition: "opacity .1s",
                cursor: "default",
              }}
            />
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#cbd5e1", marginTop: 6 }}>
        <span>{data[0]?.date}</span>
        <span style={{ color: "#6366f1", fontWeight: 600 }}>Today</span>
      </div>
    </div>
  );
};

// ─── API limit bar ────────────────────────────────────────────────────────────
const ApiLimitBar = ({ limit }) => {
  const { daily_limit, daily_limit_remaining, daily_limit_reset } = limit;
  const used = daily_limit - daily_limit_remaining;
  const pct  = daily_limit > 0 ? Math.round((used / daily_limit) * 100) : 0;
  const color = daily_limit_remaining === 0 ? "#ef4444" : pct > 80 ? "#f59e0b" : "#22c55e";

  return (
    <div style={{
      background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
      padding: "20px 24px", marginBottom: 24,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <SectionHeading>ExactOnline API Usage Today</SectionHeading>
        <span style={{ fontSize: 12, color: "#64748b" }}>
          {daily_limit_remaining?.toLocaleString()} remaining
          {daily_limit_reset && ` · resets at ${fmtTime(daily_limit_reset)}`}
        </span>
      </div>
      <div style={{ background: "#f1f5f9", borderRadius: 99, height: 8, overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`, height: "100%", borderRadius: 99,
          background: color, transition: "width .4s ease",
        }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
        <span>{used?.toLocaleString()} used</span>
        <span>{pct}% of {daily_limit?.toLocaleString()}</span>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const StoreDashboard = () => {
  usePermissionGuard("view_dashboard");
  const [data, setData] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get("/admin/store/dashboard/summary")
      .then(r => setData(r.data.data))
      .catch(() => setData(false));
  }, []);

  if (data === null) return (
    <Container sx={{ display: "flex", justifyContent: "center", pt: 10 }}>
      <CircularProgress sx={{ color: "#6366f1" }} />
    </Container>
  );

  if (data === false) return (
    <Container sx={{ display: "flex", justifyContent: "center", pt: 10 }}>
      <Box sx={{ color: "#94a3b8", fontSize: 14 }}>
        {t("You do not have permission to view the dashboard.")}
      </Box>
    </Container>
  );

  const sub = data.subscription;
  const subAccent = !sub.active ? "#ef4444" : sub.days_left <= 14 ? "#f59e0b" : "#22c55e";

  return (
    <div>
      <Container sx={{ pb: "2rem" }}>

        {/* Breadcrumb */}
        <div role="presentation">
          <Breadcrumbs
            aria-label="breadcrumb"
            separator={<NavigateNext fontSize="small" />}
            sx={{ p: "14px 0" }}
          >
            <BCLink underline="none" color="inherit" href="/" fontSize="14px">{t("Home")}</BCLink>
            <BCLink underline="none" color="text.primary" aria-current="page" fontSize="14px">Dashboard</BCLink>
          </Breadcrumbs>
        </div>

        {/* Alert banners */}
        {!sub.active && (
          <Alert type="error">{t("Subscription is not active for this store.")}</Alert>
        )}
        {sub.active && sub.days_left <= 14 && (
          <Alert type="warning">
            {t("subscription_left").replace(":days", sub.days_left)} — expires {fmtDate(sub.end_date)}
          </Alert>
        )}
        {!data.eo_connected && (
          <Alert type="error">
            {t("exact_connection_has_been_lost")} —{" "}
            <span
              style={{ textDecoration: "underline", cursor: "pointer" }}
              onClick={() => navigate("/exact/settings")}
            >
              {t("Re-authorize")}
            </span>
          </Alert>
        )}
        {data.failed_orders > 0 && (
          <Alert type="error">
            <strong>{data.failed_orders}</strong> failed {data.failed_orders === 1 ? "order" : "orders"} pending retry.{" "}
            <span
              style={{ textDecoration: "underline", cursor: "pointer" }}
              onClick={() => navigate("/store/failed-orders")}
            >
              View
            </span>
          </Alert>
        )}
        {data.api_limit?.daily_limit_remaining === 0 && (
          <Alert type="error">
            ExactOnline daily API limit exhausted. Syncing is paused until midnight.
            Resets at {fmtTime(data.api_limit.daily_limit_reset)}.
          </Alert>
        )}

        {/* KPI cards */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "14px", mb: 3 }}>
          <KpiCard
            label="Orders Today"
            value={data.orders_today}
            icon="📦"
            accent="#6366f1"
            onClick={() => navigate("/store/orders")}
          />
          <KpiCard
            label="Orders This Week"
            value={data.orders_week}
            icon="📅"
            accent="#6366f1"
            onClick={() => navigate("/store/orders")}
          />
          <KpiCard
            label="Orders This Month"
            value={data.orders_month}
            icon="📊"
            accent="#6366f1"
            onClick={() => navigate("/store/orders")}
          />
          <KpiCard
            label="Total Customers"
            value={data.total_customers}
            sub={`${data.active_customers} can order`}
            icon="👥"
            accent="#0ea5e9"
            onClick={() => navigate("/store/accounts")}
          />
          <KpiCard
            label="Failed Orders"
            value={data.failed_orders}
            icon="⚠️"
            accent={data.failed_orders > 0 ? "#ef4444" : "#94a3b8"}
            onClick={data.failed_orders > 0 ? () => navigate("/store/failed-orders") : null}
          />
          <KpiCard
            label="Subscription"
            value={sub.active ? (sub.days_left !== null ? `${sub.days_left}d` : "Active") : "Inactive"}
            sub={sub.end_date ? `Expires ${fmtDate(sub.end_date)}` : null}
            icon="🔑"
            accent={subAccent}
          />
        </Box>

        {/* Order chart */}
        <OrderChart
          data={data.order_chart}
          ordersWeek={data.orders_week}
          ordersMonth={data.orders_month}
        />

        {/* API limit */}
        {data.api_limit && <ApiLimitBar limit={data.api_limit} />}

      </Container>
    </div>
  );
};

export default React.memo(StoreDashboard);
